import { useMemo } from 'react'
import DOMPurify from 'dompurify'
import RealStoriesPhotoCard, {
  isRenderableRealStoriesCard,
} from 'src/components/campanha_2.0/RealStories_2.0/RealStoriesPhotoCard'
import type { RealStoriesPhotoCardData } from 'src/components/campanha_2.0/RealStories_2.0/RealStoriesPhotoCard'

interface RealStoriesProps {
  topLabel?: string | null
  title?: string | null
  cards?: RealStoriesPhotoCardData[] | null
}

const CARD_ROTATION_CLASSES = [
  '-rotate-[3.5deg]',
  'rotate-[2deg] mt-3',
  '-rotate-[1.5deg]',
  'rotate-[3deg] mt-3',
] as const

const BLOCKED_TAGS = ['script', 'iframe', 'object', 'embed', 'form', 'style']

const sanitizeContent = (content?: string | null) => {
  return typeof DOMPurify?.sanitize === 'function'
    ? DOMPurify.sanitize(content ?? '', {
        FORBID_TAGS: BLOCKED_TAGS,
      })
    : ''
}

export default function RealStories20({
  topLabel,
  title,
  cards,
}: Readonly<RealStoriesProps>) {
  const safeTopLabel = useMemo(() => sanitizeContent(topLabel), [topLabel])

  const safeTitle = useMemo(() => sanitizeContent(title), [title])

  const cardsToRender = useMemo(
    () => (cards ?? []).filter((card) => isRenderableRealStoriesCard(card)),
    [cards]
  )

  if (cardsToRender.length < 2 || cardsToRender.length > 8) {
    return null
  }

  return (
    <section
      data-testid="real-stories-2-0"
      aria-label="Histórias reais"
      className="w-full bg-darkBgComponent py-16 lg:pt-[88px] lg:pb-16"
    >
      <div className="mx-auto w-full max-w-[1240px] px-4 sm:px-6 lg:px-8">
        {safeTopLabel && (
          <p
            className="mb-3 text-center text-sm font-bold uppercase tracking-[2.8px] text-primary"
            dangerouslySetInnerHTML={{ __html: safeTopLabel }}
          />
        )}

        {safeTitle && (
          <h2
            className="mx-auto max-w-[840px] font-inter text-center font-bold uppercase leading-tight text-white text-3xl md:text-[52px] tracking-[0.33px]"
            dangerouslySetInnerHTML={{ __html: safeTitle }}
          />
        )}

        <ul className="mx-auto mt-8 flex max-w-[1280px] flex-wrap items-start justify-center gap-x-5 gap-y-8 sm:mt-10 sm:gap-x-6 sm:gap-y-10">
          {cardsToRender.map((card, index) => {
            const rotationClass =
              CARD_ROTATION_CLASSES[index % CARD_ROTATION_CLASSES.length]

            return (
              <RealStoriesPhotoCard
                key={`${card.image}-${index}`}
                card={card}
                rotationClass={rotationClass}
              />
            )
          })}
        </ul>
      </div>
    </section>
  )
}
